/* eslint-disable no-undef */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { ApiResponse } from "../../core/_models";
import { useSnackbar } from "../../../../_theme/context/SnackbarContext";
import SimpleLoader from "../../apps/components/SimpleLoader";
import { Modal, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { KTIcon } from "../../../../_theme/helpers";
import ProjectGoogleMap from "../../apps/components/ProjectGoogleMap";
import { getStgOneLocationData } from "../../project/components/IP-steps/core/_request";
import { getApiErrorMessage, hasGeoCoordinates } from "../../../../utils/utils";
import { isAbortedError } from "../../../../utils/global";

type Props = {
    showModal: boolean;
    setShowModal: any;
    selectedProject: {
        project_id: string;
        name: string;
        budget: string
    };
};

interface GeoLocation {
    loc_id: string;
    location_name: string;
    erf_number: string;
    geo_lat: string;
    geo_long: string;
}

interface GeoLocationApiResponse extends ApiResponse {
    data: GeoLocation[];
}

const GeoLocationPopUp: FC<Props> = ({
    selectedProject,
    showModal,
    setShowModal,
}) => {
    const [loader, setLoader] = useState<boolean>(false);
    const [tab, setTab] = useState<string>("map");
    const [locations, setLocations] = useState<any[]>([]);
    const { showSnackbar } = useSnackbar();
    const abortRef = useRef<AbortController | null>(null);

    const fetchLocations = useCallback(async () => {
        if (!selectedProject?.project_id) return;

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            setLoader(true);

            const response = await getStgOneLocationData(
                Number(selectedProject?.project_id),
                controller.signal
            );

            if (controller.signal.aborted) return;

            if (response.status === "ok") {
                // Map API keys to UI keys
                setLocations(response.data.locations);
            } else {
                console.error("Failed to fetch locations", response);
                showSnackbar("Failed to fetch locations", "danger");
            }
        } catch (error) {
            if (isAbortedError(error)) return;
            showSnackbar(getApiErrorMessage(error, "Error fetching locations"), "danger");
        } finally {
            if (!controller.signal.aborted) setLoader(false);
        }
    }, [selectedProject?.project_id, showSnackbar]);

    useEffect(() => {
        if (showModal) {
            fetchLocations();
        }
        return () => abortRef.current?.abort();
    }, [showModal, fetchLocations]);

    return (
        <div className="w-100 position-relative">
            <Modal
                show={showModal}
                onHide={() => { setShowModal(false); setTab('map'); }}
                centered
                dialogClassName="modal-dialog modal-dialog-centered mw-900px"
            >
                {loader && <SimpleLoader />}
                <Modal.Header closeButton>
                    <Modal.Title>Geo Location ({selectedProject?.name})</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex justify-content-end mb-5">
                        <div className='btn-group btn-group-sm gap-2 ' role='group' aria-label='View mode'>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{"List View"} </Tooltip>}
                            >
                                <button
                                    type='button'
                                    className={`btn btn-sm btn-icon btn-light btn-color-muted btn-active-primary ${tab === 'list' ? 'active' : ''}`}
                                    data-kt-menu-trigger='click'
                                    data-kt-menu-placement='bottom-end'
                                    data-kt-menu-flip='top-end'
                                    onClick={() => setTab('list')}
                                >
                                    <KTIcon iconType='duotone' iconName='row-horizontal' className='fs-1' />
                                </button>
                            </OverlayTrigger>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{"Map View"} </Tooltip>}
                            >
                                <button
                                    type='button'
                                    className={`btn btn-sm btn-icon btn-light btn-color-muted btn-active-primary ${tab === 'map' ? 'active' : ''}`}
                                    data-kt-menu-trigger='click'
                                    data-kt-menu-placement='bottom-end'
                                    data-kt-menu-flip='top-end'
                                    onClick={() => setTab('map')}
                                >
                                    <KTIcon iconType='duotone' iconName='map' className='fs-1' />
                                </button>
                            </OverlayTrigger>
                        </div>
                    </div>
                    {locations.length > 0 ? (
                        <div>
                            {tab === "map" && (
                                <ProjectGoogleMap
                                    allGeoLocations={locations
                                        .filter((loc: GeoLocation) => hasGeoCoordinates(loc.geo_lat, loc.geo_long))
                                        .map((loc: any) => ({
                                            ...loc,
                                            geo_lat: Number(loc.geo_lat),
                                            geo_long: Number(loc.geo_long),
                                            project_index: loc.project_id,
                                            project_name: loc.project_name,
                                            budget: selectedProject.budget
                                        }))}
                                />
                            )}
                            {tab === 'list' && <Table
                                bordered
                                hover
                                responsive
                                className="table table-bordered border-secondary w-100"
                            >
                                <thead>
                                    <tr className="bg-secondary align-middle">
                                        <th className="text-gray-700">#</th>
                                        <th className="text-gray-700">Location</th>
                                        <th className="text-gray-700">ERF Number</th>
                                        <th className="text-gray-700">Latitude</th>
                                        <th className="text-gray-700">Longitude</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {locations.map((loc, i) => (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td>{loc.geo_location}</td>
                                            <td>{loc.erf_number}</td>
                                            <td>{loc.geo_lat}</td>
                                            <td>{loc.geo_long}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>}
                        </div>
                    ) : (
                        <div className="text-center text-muted mb-5 fs-4">
                            No locations added yet.
                        </div>
                    )}
                </Modal.Body>
            </Modal>

        </div>
    );
};

export { GeoLocationPopUp };
